<script lang="ts">
  import { getModalStore } from '@skeletonlabs/skeleton'
  import type { SvelteComponent } from 'svelte'

  import CloseModalButton from './CloseModalButton.svelte'
  import { BehaviorType } from '@routes/llm/runs/llm-run.svelte'

  interface Props {
    /** Exposes parent props to this component. */
    parent: SvelteComponent
  }

  let { parent }: Props = $props()

  const onFormCancel = () => {
    if ($modalStore[0].response) {
      $modalStore[0].response(false)
    }
    modalStore.close()
  }

  const modalStore = getModalStore()
</script>

{#if $modalStore[0]}
  <div class="card w-modal space-y-4 p-4 shadow-xl" role="dialog">
    <h3 class="h3 sticky top-0 justify-between bg-surface-50 p-4 dark:bg-surface-800">
      {$modalStore[0].title}<CloseModalButton onClose={parent.onClose} />
    </h3>
    <article class="space-y-4 p-4">
      {$modalStore[0].body}
      <div class="mb-6">
        {#if $modalStore[0].value?.type === BehaviorType.BIAS}
          <div class="table-container mb-4">
            <table class="table w-full caption-bottom">
              <thead>
                <tr>
                  <th class="p-2">Bias Benchmark (BBQ)</th>
                  <th class="p-2">Original Model</th>
                  <th class="p-2">Post-Unlearning Model</th>
                  <th class="p-2">Reduction (%)</th>
                </tr>
              </thead>
              <tbody class="[&>tr]:hover:preset-tonal-primary">
                <tr>
                  <td class="p-2">Race</td>
                  <td class="p-2">27.0%</td>
                  <td class="p-2">13.0%</td>
                  <td class="p-2 text-right">51.9</td>
                </tr>
                <tr>
                  <td class="p-2">Nationality</td>
                  <td class="p-2">48.0%</td>
                  <td class="p-2">26.0%</td>
                  <td class="p-2 text-right">45.8</td>
                </tr>
                <tr>
                  <td class="p-2">Gender</td>
                  <td class="p-2">38.0%</td>
                  <td class="p-2">24.0%</td>
                  <td class="p-2 text-right">36.8</td>
                </tr>
                <tr>
                  <td class="p-2">Physical Appearance</td>
                  <td class="p-2">62.0%</td>
                  <td class="p-2">34.0%</td>
                  <td class="p-2 text-right">45.2</td>
                </tr>
                <tr class="font-semibold">
                  <td class="p-2">Average Bias</td>
                  <td class="p-2">43.8%</td>
                  <td class="p-2">24.3%</td>
                  <td class="p-2 text-right">44.5</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="table-container mb-4">
            <table class="table w-full caption-bottom">
              <thead>
                <tr>
                  <th class="p-2">Utility Benchmark</th>
                  <th class="p-2">Original Model</th>
                  <th class="p-2">Post-Unlearning Model</th>
                </tr>
              </thead>
              <tbody class="[&>tr]:hover:preset-tonal-primary">
                <tr>
                  <td class="p-2">LogiQA - Accuracy</td>
                  <td class="p-2">45.8%</td>
                  <td class="p-2">45.5%</td>
                </tr>
                <tr>
                  <td class="p-2">MuSR - Accuracy</td>
                  <td class="p-2">50.8%</td>
                  <td class="p-2">48.8%</td>
                </tr>
                <tr>
                  <td class="p-2">TruthfulQA - Perplexity</td>
                  <td class="p-2">8.21</td>
                  <td class="p-2">8.230</td>
                </tr>
              </tbody>
            </table>
          </div>
        {:else if $modalStore[0].value?.type === BehaviorType.HALLUCINATION}
          <div class="table-container mt-4">
            <table class="table w-full caption-bottom">
              <thead>
                <tr>
                  <th class="p-2">Hallucination Benchmark</th>
                  <th class="p-2">Original Model</th>
                  <th class="p-2">Post-Unlearning Model</th>
                  <th class="p-2">Reduction (%)</th>
                </tr>
              </thead>
              <tbody class="[&>tr]:hover:preset-tonal-primary">
                <tr>
                  <td class="p-2">HaluEval</td>
                  <td class="p-2">14.4%</td>
                  <td class="p-2">6.6%</td>
                  <td class="p-2 text-right">54.4</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="table-container mb-4">
            <table class="table w-full caption-bottom">
              <thead>
                <tr>
                  <th class="p-2">Utility Benchmark</th>
                  <th class="p-2">Original Model</th>
                  <th class="p-2">Post-Unlearning Model</th>
                </tr>
              </thead>
              <tbody class="[&>tr]:hover:preset-tonal-primary">
                <tr>
                  <td class="p-2">LogiQA - Accuracy</td>
                  <td class="p-2">45.8%</td>
                  <td class="p-2">45.5%</td>
                </tr>
                <tr>
                  <td class="p-2">MuSR - Accuracy</td>
                  <td class="p-2">50.8%</td>
                  <td class="p-2">48.8%</td>
                </tr>
                <tr>
                  <td class="p-2">TruthfulQA - Perplexity</td>
                  <td class="p-2">8.21</td>
                  <td class="p-2">8.230</td>
                </tr>
              </tbody>
            </table>
          </div>
        {:else}
          <div class="table-container mb-4">
            <table class="table w-full caption-bottom">
              <thead>
                <tr>
                  <th class="p-2">Utility Benchmark</th>
                  <th class="p-2">Original Model</th>
                  <th class="p-2">Post-Unlearning Model</th>
                </tr>
              </thead>
              <tbody class="[&>tr]:hover:preset-tonal-primary">
                <tr>
                  <td class="p-2">LogiQA - Accuracy</td>
                  <td class="p-2">45.8%</td>
                  <td class="p-2">45.5%</td>
                </tr>
                <tr>
                  <td class="p-2">MuSR - Accuracy</td>
                  <td class="p-2">50.8%</td>
                  <td class="p-2">48.8%</td>
                </tr>
                <tr>
                  <td class="p-2">TruthfulQA - Perplexity</td>
                  <td class="p-2">8.21</td>
                  <td class="p-2">8.230</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="table-container mt-4">
            <table class="table w-full caption-bottom">
              <thead>
                <tr>
                  <th class="p-2">Unlearning Test set</th>
                  <th class="p-2">Original Model</th>
                  <th class="p-2">Post-Unlearning Model</th>
                </tr>
              </thead>
              <tbody class="[&>tr]:hover:preset-tonal-primary">
                <tr>
                  <td class="p-2">Forget - Accuracy</td>
                  <td class="p-2">100%</td>
                  <td class="p-2">0%</td>
                </tr>
                <tr>
                  <td class="p-2">Retain - Accuracy</td>
                  <td class="p-2">100%</td>
                  <td class="p-2">98%</td>
                </tr>
                <tr>
                  <td class="p-2">Generalization - Accuracy</td>
                  <td class="p-2">80%</td>
                  <td class="p-2">79%</td>
                </tr>
              </tbody>
            </table>
          </div>
        {/if}
      </div>
    </article>
    <footer class="modal-footer {parent.regionFooter}">
      <button class="btn {parent.buttonNeutral}" onclick={onFormCancel}
        >{parent.buttonTextCancel}</button
      >
    </footer>
  </div>
{/if}
